/**
 * WizardApp — parcours répondant wizard (RESPONDENT_WIZARD_SPEC v1.3).
 *
 * MVP v1 — fonctionnel mais minimaliste :
 *  - 1 écran = 1 question (sauf matrice atomique) [D-1].
 *  - Header `Section X / N` + barre intra-section [D-3].
 *  - Autosave MERGE atomique via PATCH /s/:slug/session/:token/answers [D-5].
 *  - Cookie de reprise même appareil via localStorage (en attendant cookie HttpOnly P2b).
 *
 * Layout v1.4 — **sticky top + sticky bottom** :
 *  - Le header (Section X/N + barre de progression) est sticky en haut du viewport.
 *  - Les boutons Précédent / Suivant (ou Envoyer en review) sont sticky en bas
 *    du viewport — toujours à la même place quelle que soit la hauteur de la question.
 *  - La carte centrale prend la hauteur du contenu ; le scroll est natif (page).
 *  - `pb-32` + `env(safe-area-inset-bottom)` réservent l'espace pour la barre bottom.
 *
 * Non encore inclus (P3b/P4) : intros et récaps de section visuels, animations.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Item, SurveyDefinition } from '@webforge-survey/shared';

import {
  getSurveyDefinition,
  createSession,
  getSession,
  patchWizardAnswers,
  submitSession,
  getErrorMessage,
} from '@/lib/api';
import {
  readResumeToken,
  storeResumeToken,
  clearResumeToken,
} from '@/lib/resume-token';
import { PauseModal } from './PauseModal';
import { clearTabState, deriveSessionHash, useOneTabLock } from './one-tab';

type ScreenItem =
  | { kind: 'section_intro'; sectionId: string; sectionIdx: number; sectionTotal: number; title: string; intro: string }
  | { kind: 'item'; sectionIdx: number; sectionTotal: number; sectionTitle: string; itemIdx: number; itemTotal: number; item: Item }
  | { kind: 'review' };

/** Aplatit la définition en une liste linéaire d'écrans wizard. */
function flattenScreens(def: SurveyDefinition): ScreenItem[] {
  const screens: ScreenItem[] = [];
  // Section = unité de progression. On collecte toutes les sections (à plat) :
  // blocks > sections (si présentes) sinon items du bloc traités comme une section.
  const sections: { id: string; title: string; intro?: string; items: Item[] }[] = [];
  for (const block of def.blocks ?? []) {
    const subs = block.sections ?? [];
    if (subs.length > 0) {
      for (const sec of subs) {
        sections.push({
          id: sec.id,
          title: sec.title ?? block.title ?? '',
          ...(sec.intro ? { intro: sec.intro } : {}),
          items: sec.items ?? [],
        });
      }
    } else if ((block.items ?? []).length > 0) {
      sections.push({
        id: block.id,
        title: block.title ?? '',
        items: block.items ?? [],
      });
    }
  }
  const total = sections.length;
  sections.forEach((sec, sIdx) => {
    if (sec.intro && sec.intro.length > 0) {
      screens.push({
        kind: 'section_intro',
        sectionId: sec.id,
        sectionIdx: sIdx,
        sectionTotal: total,
        title: sec.title,
        intro: sec.intro,
      });
    }
    sec.items.forEach((item, iIdx) => {
      screens.push({
        kind: 'item',
        sectionIdx: sIdx,
        sectionTotal: total,
        sectionTitle: sec.title,
        itemIdx: iIdx,
        itemTotal: sec.items.length,
        item,
      });
    });
  });
  screens.push({ kind: 'review' });
  return screens;
}

export default function WizardApp() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) {
    return <div className="p-8 text-center">Lien invalide.</div>;
  }
  return <WizardInner slug={slug} />;
}

function WizardInner({ slug }: { slug: string }): JSX.Element {
  const defQuery = useQuery({
    queryKey: ['wizard-def', slug],
    queryFn: () => getSurveyDefinition(slug),
  });

  const [resumeToken, setResumeToken] = useState<string | null>(() => readResumeToken(slug));
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [screenIdx, setScreenIdx] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [submitted, setSubmitted] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [erased, setErased] = useState(false);
  // Replaces the DOM-based hack — checkbox state for the final review screen.
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // One-tab-only (§7 v1.3) — sessionHash dérivé du token, jamais du sessionId.
  const [sessionHash, setSessionHash] = useState<string | null>(null);
  useEffect(() => {
    if (!resumeToken) {
      setSessionHash(null);
      return;
    }
    void deriveSessionHash(resumeToken).then((h) => setSessionHash(h));
  }, [resumeToken]);
  const { isPrimary, takeOver } = useOneTabLock(sessionHash);

  // Restaure session si token déjà présent (cookie/localStorage même appareil).
  useEffect(() => {
    if (!resumeToken || sessionId || !defQuery.data) return;
    getSession(slug, resumeToken)
      .then((s) => {
        if (!s) {
          clearResumeToken(slug);
          setResumeToken(null);
          return;
        }
        setSessionId(s.sessionId);
        setAnswers(s.answers as Record<string, unknown>);
      })
      .catch(() => {
        clearResumeToken(slug);
        setResumeToken(null);
      });
  }, [resumeToken, sessionId, defQuery.data, slug]);

  // Crée la session au 1er rendu si aucun token (consentement implicite v1 MVP).
  // P3b : ajouter un ConsentScreen avant.
  const startMutation = useMutation({
    mutationFn: () => createSession(slug),
    onSuccess: (s) => {
      setSessionId(s.sessionId);
      storeResumeToken(slug, s.resumeToken);
      setResumeToken(s.resumeToken);
    },
  });
  useEffect(() => {
    if (!defQuery.data || sessionId || resumeToken || startMutation.isPending) return;
    startMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defQuery.data, sessionId, resumeToken]);

  const screens = useMemo<ScreenItem[]>(
    () => (defQuery.data ? flattenScreens(defQuery.data) : []),
    [defQuery.data],
  );
  const current = screens[screenIdx];

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!resumeToken || !sessionId || !defQuery.data) throw new Error('no session');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return submitSession(slug, sessionId, resumeToken, { answers: answers as any });
    },
    onSuccess: () => {
      setSubmitted(true);
      clearResumeToken(slug);
      // §7 v1.3 — cleanup localStorage à la soumission finale.
      if (sessionHash) clearTabState(sessionHash);
    },
  });

  if (defQuery.isLoading) return <div className="p-8 text-center text-text-muted">Chargement…</div>;
  if (defQuery.isError) return <div className="p-8 text-center text-danger-text">{getErrorMessage(defQuery.error)}</div>;
  if (!defQuery.data) return <div className="p-8 text-center">—</div>;
  if (!sessionId) return <div className="p-8 text-center text-text-muted">Préparation de votre session…</div>;
  if (!current) return <div className="p-8 text-center">Fin du parcours.</div>;
  if (erased) {
    return (
      <main className="min-h-screen bg-bg flex items-center justify-center p-8">
        <div className="card max-w-md w-full text-center space-y-3">
          <h1 className="text-xl font-semibold">Vos réponses ont été effacées</h1>
          <p className="text-sm text-text-muted">
            Aucune donnée ne subsiste de cette session. Vous pouvez fermer cet onglet.
          </p>
        </div>
      </main>
    );
  }
  if (submitted) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold mb-4">Merci.</h1>
        <p className="text-text-muted">Vos réponses ont bien été enregistrées.</p>
      </div>
    );
  }

  async function persist(partialKey: string, value: unknown): Promise<void> {
    if (!resumeToken) return;
    const partial = { [partialKey]: value };
    const newAnswers = { ...answers, ...partial };
    setAnswers(newAnswers);
    setSaveStatus('saving');
    try {
      const idem = `wiz-${sessionId}-${partialKey}-${Date.now()}`;
      await patchWizardAnswers(slug, resumeToken, partial, idem);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }

  const goNext = (): void => {
    setScreenIdx((i) => Math.min(i + 1, screens.length - 1));
  };
  const goPrev = (): void => setScreenIdx((i) => Math.max(0, i - 1));

  // -----------------------------------------------------------------
  // BODY (contenu central, scroll naturel de la page) — sans boutons.
  // -----------------------------------------------------------------
  let body: JSX.Element;
  if (current.kind === 'section_intro') {
    body = (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{current.title}</h2>
        <p className="text-text-muted whitespace-pre-line">{current.intro}</p>
      </div>
    );
  } else if (current.kind === 'item') {
    body = (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{current.sectionTitle}</h2>
        <div className="text-xs text-text-muted">Question {current.itemIdx + 1} / {current.itemTotal}</div>
        <SimpleItemRenderer
          item={current.item}
          value={answers[current.item.name]}
          onChange={(v) => void persist(current.item.name, v)}
        />
      </div>
    );
  } else {
    body = (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Vérifier mes réponses</h2>
        <p className="text-text-muted">Vous avez répondu à {Object.keys(answers).length} question(s).</p>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmSubmit}
            onChange={(e) => setConfirmSubmit(e.target.checked)}
          />
          <span>Je confirme vouloir soumettre ces réponses définitivement.</span>
        </label>
        {submitMutation.isError && (
          <p className="field-error">{getErrorMessage(submitMutation.error)}</p>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------
  // FOOTER sticky : Précédent / Suivant — contenu dépend du screen.
  // -----------------------------------------------------------------
  // Boutons Précédent en `btn-secondary` (bordure visible) — plus repérable
  // que `btn-ghost` (texte seul). Min-width pour alignement visuel.
  let footerActions: JSX.Element;
  if (current.kind === 'review') {
    footerActions = (
      <>
        <button type="button" className="btn-secondary min-w-[120px]" onClick={goPrev}>
          Précédent
        </button>
        <button
          type="button"
          className="btn-primary min-w-[140px]"
          disabled={!confirmSubmit || submitMutation.isPending}
          onClick={() => submitMutation.mutate()}
        >
          {submitMutation.isPending ? 'Envoi…' : 'Envoyer'}
        </button>
      </>
    );
  } else {
    const isIntro = current.kind === 'section_intro';
    footerActions = (
      <>
        <button
          type="button"
          className="btn-secondary min-w-[120px]"
          onClick={goPrev}
          disabled={screenIdx === 0}
        >
          Précédent
        </button>
        <button type="button" className="btn-primary min-w-[140px]" onClick={goNext}>
          {isIntro ? 'Commencer' : 'Suivant'}
        </button>
      </>
    );
  }

  // Indicateur de sauvegarde (visible uniquement sur les écrans question).
  const saveIndicator = current.kind === 'item' ? (
    <div className="text-xs text-text-muted text-right">
      {saveStatus === 'saving' && 'Sauvegarde…'}
      {saveStatus === 'saved' && '✓ Sauvegardé'}
      {saveStatus === 'error' && '⚠ Erreur de sauvegarde'}
    </div>
  ) : null;

  // -----------------------------------------------------------------
  // HEADER sticky : Section X / N + barre intra-section.
  // -----------------------------------------------------------------
  const headerInfo: { sectionIdx: number; sectionTotal: number; intraPct: number } | null =
    current.kind === 'item'
      ? {
          sectionIdx: current.sectionIdx,
          sectionTotal: current.sectionTotal,
          intraPct: Math.round(((current.itemIdx + 1) / current.itemTotal) * 100),
        }
      : current.kind === 'section_intro'
      ? {
          sectionIdx: current.sectionIdx,
          sectionTotal: current.sectionTotal,
          intraPct: 0,
        }
      : null;

  return (
    // Layout v1.4.2 — flex column avec centrage vertical de la carte.
    // `my-auto` sur la carte centre verticalement quand l'espace dispo > taille
    // du contenu (écrans courts), et se replie naturellement quand le contenu
    // déborde (matrices, blocs sensibles).
    <main className="min-h-screen bg-bg flex flex-col">
      {/* HEADER sticky — Section X/N + barre de progression. Fond blanc
          opaque + ombre pour bien le détacher du contenu (Layout v1.4.1). */}
      <div
        className="sticky top-0 z-30 bg-surface border-b border-border shadow-[0_2px_8px_rgba(15,30,45,0.05)] flex-shrink-0"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-xl mx-auto px-4 py-3.5">
          {headerInfo ? (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-semibold text-text">
                  Section {headerInfo.sectionIdx + 1}
                  <span className="font-normal text-text-muted"> / {headerInfo.sectionTotal}</span>
                </span>
                <span className="text-xs text-text-muted tabular-nums">
                  {headerInfo.intraPct}%
                </span>
              </div>
              <div className="h-2.5 bg-surface-2 rounded-full overflow-hidden ring-1 ring-border/40">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${headerInfo.intraPct}%` }}
                />
              </div>
            </div>
          ) : (
            // Sur l'écran "review", on garde la même hauteur de header pour ne
            // pas faire sauter la mise en page.
            <div className="text-sm font-semibold text-text py-[0.625rem]">
              Vérification avant envoi
            </div>
          )}
        </div>
      </div>

      {/* MIDDLE — flex-1 + centrage vertical de la carte via my-auto.
          pb-40 réserve l'espace sous la barre footer fixed pour éviter que
          le pied de page (Pause + mention) soit caché. */}
      <div className="flex-1 flex flex-col px-4 py-6 pb-40">
        <div className="max-w-xl mx-auto w-full my-auto space-y-4">
          <div className="card">{body}</div>
          {saveIndicator}
          <div className="text-center">
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setPauseOpen(true)}
            >
              Pause / Afficher mon code de reprise
            </button>
          </div>
          <p className="text-[10px] text-text-subtle text-center">
            Wizard v1 — autosave atomique chiffré activé. Cookie de reprise opt-in (cf. consentement).
          </p>
        </div>
      </div>

      {/* FOOTER sticky — boutons Précédent / Suivant toujours au même endroit.
          Fond blanc opaque + ombre vers le haut pour démarquer (Layout v1.4.1). */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 bg-surface border-t border-border shadow-[0_-2px_12px_rgba(15,30,45,0.07)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="max-w-xl mx-auto px-4 py-3.5 flex justify-between gap-3">
          {footerActions}
        </div>
      </div>

      <PauseModal
        slug={slug}
        resumeToken={resumeToken}
        open={pauseOpen}
        onClose={() => setPauseOpen(false)}
        onErased={() => {
          clearResumeToken(slug);
          // §7 v1.3 — cleanup localStorage à l'erase art. 17.
          if (sessionHash) clearTabState(sessionHash);
          setErased(true);
          setPauseOpen(false);
        }}
      />
      {/* Voile « cette enquête est déjà ouverte dans un autre onglet ». */}
      {!isPrimary && (
        <div className="fixed inset-0 z-40 bg-bg/95 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="card max-w-md w-full space-y-4 text-center">
            <h2 className="text-lg font-semibold">Cette enquête est déjà ouverte dans un autre onglet</h2>
            <p className="text-sm text-text-muted">
              Pour éviter d'écraser vos réponses, un seul onglet peut éditer cette session à la fois.
            </p>
            <button type="button" className="btn-primary" onClick={takeOver}>
              Reprendre ici
            </button>
            <p className="text-xs text-text-subtle">
              L'autre onglet repassera en mode « en attente ».
            </p>
          </div>
        </div>
      )}
    </main>
  );
}

/**
 * Rendu basique des items wizard (1 par écran sauf matrix atomique).
 * MVP — réutilisera le RespondentRenderer existant en v2.
 */
function SimpleItemRenderer({
  item,
  value,
  onChange,
}: {
  item: Item;
  value: unknown;
  onChange: (v: unknown) => void;
}): JSX.Element {
  switch (item.type) {
    case 'note':
      return <p className="text-text whitespace-pre-line">{item.label}</p>;
    case 'select_one':
      return (
        <div className="space-y-2">
          <p className="font-medium">{item.label}</p>
          {(item.choices ?? []).map((c) => (
            <label key={c.value} className="flex items-center gap-2 p-3 rounded-md hover:bg-surface-2 cursor-pointer">
              <input
                type="radio"
                name={item.name}
                value={c.value}
                checked={value === c.value}
                onChange={() => onChange(c.value)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      );
    case 'select_multiple':
      return (
        <div className="space-y-2">
          <p className="font-medium">{item.label}</p>
          {(item.choices ?? []).map((c) => {
            const arr = (Array.isArray(value) ? value : []) as string[];
            const checked = arr.includes(c.value);
            return (
              <label key={c.value} className="flex items-center gap-2 p-3 rounded-md hover:bg-surface-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onChange(checked ? arr.filter((v) => v !== c.value) : [...arr, c.value])}
                />
                <span>{c.label}</span>
              </label>
            );
          })}
        </div>
      );
    case 'integer':
      return (
        <div className="space-y-2">
          <p className="font-medium">{item.label}</p>
          <input
            type="number"
            className="input-base"
            value={value as number ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      );
    case 'text_short':
      return (
        <div className="space-y-2">
          <p className="font-medium">{item.label}</p>
          <input
            type="text"
            className="input-base"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'text_long':
      return (
        <div className="space-y-2">
          <p className="font-medium">{item.label}</p>
          <textarea
            rows={5}
            className="input-base"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'matrix':
      return (
        <div className="space-y-3">
          <p className="font-medium">{item.label}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th></th>
                  {(item.choices ?? []).map((c) => (
                    <th key={c.value} className="px-2 py-1 text-xs font-normal text-text-muted">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(item.rows ?? []).map((row) => {
                  const obj = (typeof value === 'object' && value !== null ? value : {}) as Record<string, string>;
                  return (
                    <tr key={row.name} className="border-t border-border">
                      <td className="py-2 pr-2">{row.label}</td>
                      {(item.choices ?? []).map((c) => (
                        <td key={c.value} className="text-center">
                          <input
                            type="radio"
                            name={`${item.name}-${row.name}`}
                            checked={obj[row.name] === c.value}
                            onChange={() => onChange({ ...obj, [row.name]: c.value })}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    default:
      return <p className="text-text-muted">[Type d'item non supporté en MVP]</p>;
  }
}
